import React, { useMemo, useRef, useState } from 'react';
import { Save, Loader2, RotateCcw } from 'lucide-react';
import { Switch } from '../ui/switch';
import { TokenChips, insertAtCaret } from './TokenChips';
import { requestDriveRenameCheck } from './DriveRenamePrompt';

const RESEND_LABELS = { keep_versions: 'Keep both (v2, v3…)', replace: 'Replace old copy', skip: "Don't save again" };

export const DocumentTypesTab = ({ api, overview, onSaved, toast }) => {
  const [rows, setRows] = useState(overview.documents);
  const [dirty, setDirty] = useState({});
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState('');
  const focused = useRef({ kind: null, el: null });
  const stages = overview.layout.stages;

  const patch = (kind, field, value) => {
    setRows((rs) => rs.map((r) => (r.kind === kind ? { ...r, [field]: value } : r)));
    setDirty((d) => ({ ...d, [kind]: true }));
  };
  const insert = (token) => {
    const { kind, el } = focused.current;
    if (!kind) return;
    const row = rows.find((r) => r.kind === kind);
    patch(kind, 'filename', insertAtCaret(el, row.filename || row.default_filename, token));
  };
  const visible = useMemo(() => rows.filter((r) => !filter || r.stage === filter), [rows, filter]);

  const save = async () => {
    setSaving(true);
    try {
      const rules = {};
      rows.filter((r) => dirty[r.kind]).forEach((r) => { rules[r.kind] = { filename: r.filename, stage: r.stage, save: r.save, resend: r.resend }; });
      await api.put('/drive/document-rules', { rules });
      toast.success('Document rules saved');
      setDirty({});
      onSaved();
      requestDriveRenameCheck({ all: true });
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-4" data-testid="drive-document-types-tab">
      <div className="bg-white border border-[#E2E2DF] rounded-md overflow-hidden">
        <div className="flex items-center justify-between gap-2 px-4 py-2.5 border-b border-[#E2E2DF] bg-slate-50">
          <select data-testid="doc-types-stage-filter" value={filter} onChange={(e) => setFilter(e.target.value)}
            className="text-xs border border-[#E2E2DF] rounded px-2 py-1.5 bg-white">
            <option value="">All stage folders ({rows.length})</option>
            {stages.map((s) => <option key={s.key} value={s.key}>{s.name}</option>)}
          </select>
          <button data-testid="doc-types-save-btn" onClick={save} disabled={saving || !Object.keys(dirty).length}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-semibold rounded-md bg-[#0F4F1B] text-white hover:bg-[#0a3c14] disabled:opacity-40 transition-colors">
            {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save {Object.keys(dirty).length || ''} change{Object.keys(dirty).length === 1 ? '' : 's'}
          </button>
        </div>
        <div className="divide-y divide-[#F1EFEC]">
          {visible.map((r) => (
            <div key={r.kind} data-testid={`doc-type-row-${r.kind}`} className={`grid grid-cols-1 xl:grid-cols-[180px_1fr_170px_60px_170px] gap-2 items-center px-4 py-2.5 ${r.save ? '' : 'opacity-60'}`}>
              <div className="min-w-0">
                <div className="text-xs font-semibold text-slate-800 truncate">{r.label}</div>
                <div className="text-[10px] font-mono text-slate-400 truncate">{r.kind}</div>
              </div>
              <div className="flex items-center gap-1">
                <input data-testid={`doc-type-filename-${r.kind}`} value={r.filename || ''} placeholder={r.default_filename}
                  onFocus={(e) => { focused.current = { kind: r.kind, el: e.target }; }}
                  onChange={(e) => patch(r.kind, 'filename', e.target.value)}
                  className="w-full px-2 py-1.5 text-[11px] font-mono border border-[#E2E2DF] rounded focus:outline-none focus:ring-2 focus:ring-[#0F4F1B]/30" />
                {r.filename && (
                  <button data-testid={`doc-type-reset-${r.kind}`} title="Back to default" onClick={() => patch(r.kind, 'filename', '')}
                    className="p-1 text-slate-400 hover:text-[#D35400]"><RotateCcw size={12} /></button>
                )}
              </div>
              <select data-testid={`doc-type-stage-${r.kind}`} value={r.stage} onChange={(e) => patch(r.kind, 'stage', e.target.value)}
                className="text-[11px] border border-[#E2E2DF] rounded px-2 py-1.5 bg-white">
                {stages.map((s) => <option key={s.key} value={s.key}>{s.name}</option>)}
              </select>
              <div className="flex xl:justify-center items-center gap-2" title="Save to Drive"><span className="xl:hidden text-[11px] text-slate-500">Save to Drive</span>
                <Switch data-testid={`doc-type-save-${r.kind}`} checked={!!r.save} onCheckedChange={(v) => patch(r.kind, 'save', v)} />
              </div>
              <select data-testid={`doc-type-resend-${r.kind}`} value={r.resend} onChange={(e) => patch(r.kind, 'resend', e.target.value)}
                className="text-[11px] border border-[#E2E2DF] rounded px-2 py-1.5 bg-white">
                {overview.resend_modes.map((m) => <option key={m} value={m}>{RESEND_LABELS[m]}</option>)}
              </select>
            </div>
          ))}
        </div>
      </div>
      <TokenChips tokens={overview.tokens} onInsert={insert} />
    </div>
  );
};
